import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import i18n from '../../i18n';
import { ReactComponent as DocumentIcon } from '../../static/images/svg/archive-document.svg';
import { ReactComponent as ManualEntryIcon } from '../../static/images/svg/archive-manual-entry.svg';
import DialogComponent from '../dialog/dialog.component';
import './archive-create-mode-dialog.scss';
import GlobalNotification from '../common/snackbar';

/** 建档主体类型，用于选择标题与主体文案。 */
export type ArchiveCreateEntityType = 'group' | 'restaurant';

/** 新增方式弹窗参数。 */
export type ArchiveCreateModeDialogProps = {
    /** 是否显示弹窗。 */
    open: boolean;
    /** 当前新增的业务主体。 */
    entityType: ArchiveCreateEntityType;
    /** 餐厅所属集团名称；集团模式不使用。 */
    groupName?: string;
    /** 关闭弹窗。 */
    onClose: () => void;
    /** 进入现有手动新增表单。 */
    onManualEntry: () => void;
};

/**
 * 展示扫描登记文件或手动填写的建档方式选择。
 * @param props 弹窗状态、业务主体与操作回调
 * @returns 新增方式选择弹窗
 */
export function ArchiveCreateModeDialog({
    open,
    entityType,
    groupName,
    onClose,
    onManualEntry,
}: ArchiveCreateModeDialogProps): JSX.Element {
    /** 是否为餐厅新增场景。 */
    const isRestaurant = entityType === 'restaurant';
    /** 当前业务主体对应的弹窗标题。 */
    const titleKey = isRestaurant
        ? 'archive_form_create_restaurant_title'
        : 'archive_form_create_group_title';
    /** 当前业务主体对应的登记文件说明。 */
    const documentDescriptionKey = isRestaurant
        ? 'archive_form_restaurant_document_description'
        : 'archive_form_group_document_description';

    return (
        <DialogComponent
            open={open}
            maxWidth='sm'
            defaultHeaderProps={{ title: i18n.t(titleKey) }}
            onClose={onClose}
            footer={<></>}
            sx={{
                '& .MuiDialog-container > .MuiPaper-root': {
                    maxWidth: '620px',
                    border: 0,
                    borderRadius: '12px',
                    background: 'var(--fill-neutral-100)',
                },
                '& .dialog-footer': {
                    display: 'none',
                },
            }}
            className='archive-create-mode-dialog'
        >
            <Stack spacing={3}>
                {isRestaurant && (
                    <Typography variant='body2' color='text.secondary'>
                        {i18n.t('archive_form_restaurant_group_hint', { groupName })}
                    </Typography>
                )}

                <ButtonBase
                    className='archive-create-mode-choice'
                    aria-label={`${i18n.t('archive_form_scan_document')} ${i18n.t('archive_form_ocr_unavailable')}`}
                    onClick={() => {
                        GlobalNotification.error(i18n.t('archive_form_ocr_unavailable'));
                     }}
                >
                    <DocumentIcon className='archive-create-mode-icon' aria-hidden='true' />
                    <Box className='archive-create-mode-content'>
                        <Typography variant='subtitle2'>
                            {i18n.t('archive_form_scan_document')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            {i18n.t(documentDescriptionKey)}
                        </Typography>
                        <Box className='archive-create-mode-meta'>
                            <Typography variant='caption' className='archive-create-mode-badge'>
                                {i18n.t('archive_form_scan_coverage')}
                            </Typography>
                            <Typography variant='caption' className='archive-create-mode-drop'>
                                {i18n.t('archive_form_drop_document')}
                            </Typography>
                        </Box>
                    </Box>
                </ButtonBase>

                <ButtonBase
                    className='archive-create-mode-choice'
                    aria-label={i18n.t('archive_form_manual_entry')}
                    onClick={onManualEntry}
                >
                    <ManualEntryIcon className='archive-create-mode-icon' aria-hidden='true' />
                    <Box className='archive-create-mode-content'>
                        <Typography variant='subtitle2'>
                            {i18n.t('archive_form_manual_entry')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            {i18n.t('archive_form_manual_entry_description')}
                        </Typography>
                    </Box>
                </ButtonBase>
            </Stack>
        </DialogComponent>
    );
}
